/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import EmployeeCard from './components/EmployeeCard';
import EmployeeDetailPanel from './components/EmployeeDetailPanel';
import SearchAndFilters from './components/SearchAndFilters';
import BulkCommunicationPanel from './components/BulkCommunicationPanel';
import EmployeeFormModal from './components/EmployeeFormModal';
import InviteEmployeeModal from './components/InviteEmployeeModal';
import ConfirmRevokeInvitationModal from './components/ConfirmRevokeInvitationModal';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import {
  fetchEmployees as fetchEmployeesFromApi,
  fetchRemovedEmployees as fetchRemovedEmployeesFromApi,
  fetchEmployeeById,
  deleteEmployee as deleteEmployeeFromApi,
  updateEmployee as updateEmployeeFromApi,
  restoreEmployee as restoreEmployeeFromApi,
} from '@/lib/api/employees';
import { fetchTimeOffRequests } from '@/lib/api/timeOff';
import {
  fetchPendingInvitations,
  revokeEmployeeInvitation,
} from '@/lib/api/employeeInvitations';
import { useAuth } from '@/context/AuthContext';
import { useLocations } from '@/context/LocationsContext';
import LoadingState from '@/components/ui/LoadingState';

const STATUS_OPTIONS = [
  { value: 'on-work', key: 'onWork', fallback: 'On Work' },
  { value: 'off-work', key: 'offWork', fallback: 'Off Work' },
  { value: 'in-vacation', key: 'inVacation', fallback: 'In Vacation' },
  { value: 'sick-leave', key: 'sickLeave', fallback: 'Sick Leave' },
];

const normalizeMessagingIdentifier = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '[object Object]') {
      return null;
    }
    return trimmed;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return String(value);
  }

  if (typeof value === 'object') {
    const directCandidate =
      value?.id ??
      value?._id ??
      value?.userId ??
      value?.user_id ??
      value?.sub ??
      value?.$oid ??
      null;

    if (directCandidate != null) {
      return normalizeMessagingIdentifier(directCandidate);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return stringified;
      }
    }
  }

  return null;
};

const resolveMessagingRecipientIdFromEmployee = (employee) => {
  if (!employee) {
    return null;
  }

  const userReference = employee?.raw?.user ?? null;
  const candidates = [
    employee?.userId,
    employee?.user_id,
    employee?.raw?.userId,
    employee?.raw?.user_id,
    userReference,
    userReference?.id,
    userReference?._id,
    userReference?.userId,
    userReference?.user_id,
    userReference?.sub,
    employee?.id,
    employee?.employeeId,
    employee?.employee_id,
    employee?.raw?.id,
    employee?.raw?._id,
    employee?.raw?.employeeId,
    employee?.raw?.employee_id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeMessagingIdentifier(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

const filterAbsencesForEmployee = (requests, employee) => {
  if (!Array.isArray(requests) || !employee) {
    return [];
  }

  const candidateIds = new Set(
    [
      employee?.id,
      employee?.employeeId,
      employee?.raw?.id,
      employee?.raw?._id,
      employee?.raw?.employeeId,
    ].filter(Boolean)
  );

  return requests?.filter((request) => {
    if (!request) {
      return false;
    }

    const requestIds = [
      request?.employeeId,
      request?.employee?._id,
      request?.employee?.id,
      request?.employee?.employeeId,
    ];

    return requestIds?.some((id) => id && candidateIds.has(id));
  });
};

const buildEmployeeUpdatePayload = (employee) => {
  if (!employee) {
    return {};
  }

  const toIsoDate = (value) => {
    if (!value) {
      return undefined;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    return date.toISOString().split('T')[0];
  };

  const hireDateValue =
    employee?.hireDate ??
    employee?.raw?.hireDate ??
    employee?.raw?.hireDate?.value;

  const payload = {
    employeeId: employee?.employeeId ?? undefined,
    name: employee?.name ?? undefined,
    fullName: employee?.name ?? employee?.fullName ?? undefined,
    role: employee?.role ?? undefined,
    department: employee?.department ?? undefined,
    locationId: (() => {
      const candidate =
        employee?.locationId ??
        employee?.raw?.locationId ??
        employee?.locationDetails?.id ??
        employee?.raw?.locationDetails?.id;

      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        return trimmed ? trimmed : undefined;
      }

      return candidate ?? undefined;
    })(),
    locationIds: (() => {
      if (Array.isArray(employee?.locationIds)) {
        const normalized = employee.locationIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0);
        if (normalized.length > 0) {
          return normalized;
        }
      }

      if (Array.isArray(employee?.locations)) {
        const normalized = employee.locations
          .map((assignment) =>
            typeof assignment?.id === 'string' ? assignment.id.trim() : ''
          )
          .filter((id) => id.length > 0);
        if (normalized.length > 0) {
          return normalized;
        }
      }

      return undefined;
    })(),
    businessId: (() => {
      if (
        typeof employee?.businessId === 'string' &&
        employee.businessId.trim()
      ) {
        return employee.businessId.trim();
      }

      if (
        typeof employee?.business?.id === 'string' &&
        employee.business.id.trim()
      ) {
        return employee.business.id.trim();
      }

      if (
        typeof employee?.raw?.businessId === 'string' &&
        employee.raw.businessId.trim()
      ) {
        return employee.raw.businessId.trim();
      }

      return undefined;
    })(),
    birthday: toIsoDate(employee?.birthday ?? employee?.raw?.birthday),
    status: employee?.status ?? undefined,
    hireDate: toIsoDate(hireDateValue),
    shiftPreference: employee?.shiftPreference ?? undefined,
    phone: employee?.phone ?? undefined,
    email: employee?.email ?? undefined,
    address: employee?.address ?? undefined,
    tags: Array.isArray(employee?.tags) ? employee.tags : undefined,
    emergencyContact: employee?.emergencyContact ?? undefined,
    avatarFileId: Object.prototype.hasOwnProperty.call(
      employee ?? {},
      'avatarFileId'
    )
      ? employee?.avatarFileId ?? null
      : undefined,
    avatarImageBase64:
      typeof employee?.avatarImageBase64 === 'string' &&
      employee.avatarImageBase64.trim().length > 0
        ? employee.avatarImageBase64.trim()
        : undefined,
    avatarImageType:
      typeof employee?.avatarImageType === 'string' &&
      employee.avatarImageType.trim().length > 0
        ? employee.avatarImageType.trim()
        : undefined,
    avatarImageName:
      typeof employee?.avatarImageName === 'string' &&
      employee.avatarImageName.trim().length > 0
        ? employee.avatarImageName.trim()
        : undefined,
  };

  if (typeof payload.avatarImageBase64 === 'string') {
    delete payload.avatarFileId;
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
};

const getEmployeeUserId = (employee) => {
  if (!employee) {
    return null;
  }

  const userReference = employee?.user ?? employee?.raw?.user ?? null;
  const candidates = [
    employee?.userId,
    employee?.user_id,
    employee?.raw?.userId,
    employee?.raw?.user_id,
    userReference,
    userReference?.id,
    userReference?._id,
    userReference?.userId,
    userReference?.user_id,
    userReference?.sub,
    userReference?.$oid,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeMessagingIdentifier(candidate);
    if (normalized) {
      return normalized;
    }
  }

  if (
    typeof userReference === 'function' &&
    typeof userReference.toString === 'function'
  ) {
    const stringified = userReference.toString();
    if (stringified && stringified !== '[object Object]') {
      return stringified;
    }
  }

  return null;
};

const createLocalId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `temp-${Date.now()}`;
};

const extractErrorMessage = (error) => {
  if (!error) {
    return '';
  }
  if (Array.isArray(error?.response?.data?.message)) {
    return error.response.data.message.join('. ');
  }
  return error?.response?.data?.message ?? error?.message ?? '';
};

const REMOVAL_GRACE_PERIOD_MS = 30 * 24 * 60 * 60 * 1000;

const parseTimestamp = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const getRemovalStatus = (removedAt, t, language) => {
  const removedDate = parseTimestamp(removedAt);

  if (!removedDate) {
    return {
      expired: true,
      timeText: t(
        'employeeDirectory:removed.undoUnavailable',
        'Undo option unavailable.'
      ),
      deadline: null,
      detailText: '',
    };
  }

  const deadline = new Date(removedDate.getTime() + REMOVAL_GRACE_PERIOD_MS);
  const diffMs = deadline.getTime() - Date.now();
  const formattedDeadline = deadline.toLocaleString(language || undefined);

  if (diffMs <= 0) {
    return {
      expired: true,
      timeText: formattedDeadline
        ? formatTranslation(
            t(
              'employeeDirectory:removed.undoExpiredWithDate',
              'Undo option expired on {{date}}.'
            ),
            { date: formattedDeadline }
          )
        : t('employeeDirectory:removed.undoExpired', 'Undo option expired.'),
      deadline,
      detailText: '',
    };
  }

  const dayMs = 24 * 60 * 60 * 1000;
  const hourMs = 60 * 60 * 1000;
  const minuteMs = 60 * 1000;

  const days = Math.floor(diffMs / dayMs);
  const hours = Math.floor((diffMs % dayMs) / hourMs);
  const minutes = Math.floor((diffMs % hourMs) / minuteMs);

  const parts = [];

  if (days > 0) {
    parts.push(
      formatTranslation(
        t(
          `employeeDirectory:removed.duration.days.${
            days === 1 ? 'singular' : 'plural'
          }`,
          days === 1 ? '{{count}} day' : '{{count}} days'
        ),
        { count: days }
      )
    );
  }

  if (hours > 0 && parts.length < 2) {
    parts.push(
      formatTranslation(
        t(
          `employeeDirectory:removed.duration.hours.${
            hours === 1 ? 'singular' : 'plural'
          }`,
          hours === 1 ? '{{count}} hour' : '{{count}} hours'
        ),
        { count: hours }
      )
    );
  }

  if (parts.length === 0) {
    parts.push(
      formatTranslation(
        t(
          `employeeDirectory:removed.duration.minutes.${
            minutes === 1 ? 'singular' : 'plural'
          }`,
          minutes === 1 ? '{{count}} minute' : '{{count}} minutes'
        ),
        { count: minutes }
      )
    );
  }

  const countdownText = formatTranslation(
    t(
      'employeeDirectory:removed.undoCountdown',
      'Undo option expires in {{duration}}'
    ),
    { duration: parts.join(' ') }
  );
  const deadlineDetail = formattedDeadline
    ? formatTranslation(
        t('employeeDirectory:removed.deadlineDetail', 'Expires on {{date}}.'),
        { date: formattedDeadline }
      )
    : '';

  return {
    expired: false,
    timeText: countdownText,
    deadline,
    deadlineLabel: formattedDeadline,
    detailText: deadlineDetail,
  };
};

const sortRemovedEmployeesByDate = (list) => {
  const copy = Array.isArray(list) ? [...list] : [];
  copy.sort((a, b) => {
    const aTime = parseTimestamp(a?.removedAt)?.getTime?.() ?? 0;
    const bTime = parseTimestamp(b?.removedAt)?.getTime?.() ?? 0;
    return bTime - aTime;
  });
  return copy;
};

const EmployeeDirectory = () => {
  const { t, language } = useTranslation(['employeeDirectory', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = useMemo(() => {
    const candidate =
      user?.sub ??
      user?.id ??
      user?._id ??
      user?.userId ??
      user?.user_id ??
      user?.employee ??
      null;
    return normalizeMessagingIdentifier(candidate);
  }, [user]);
  const normalizedRole =
    typeof user?.role === 'string' ? user.role.toLowerCase().trim() : '';
  const canUseBulkSelection = normalizedRole !== 'manager';
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useLocations();
  const [employees, setEmployees] = useState([]);
  const [employeeDetailsCache, setEmployeeDetailsCache] = useState({});
  const [removedEmployees, setRemovedEmployees] = useState([]);
  const [removedLoading, setRemovedLoading] = useState(true);
  const [removedError, setRemovedError] = useState(null);
  const [removedSectionOpen, setRemovedSectionOpen] = useState(false);
  const [restoringEmployeeId, setRestoringEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    department: '',
    role: '',
    status: '',
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showBulkCommunication, setShowBulkCommunication] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState(null);
  const [revokingToken, setRevokingToken] = useState(null);
  const [pendingRevokeInvitation, setPendingRevokeInvitation] = useState(null);
  const [pendingActionError, setPendingActionError] = useState(null);
  const [employeeActionError, setEmployeeActionError] = useState(null);
  const [timeOffRequests, setTimeOffRequests] = useState(null);
  const [timeOffLoading, setTimeOffLoading] = useState(false);
  const [timeOffError, setTimeOffError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const editingEmployeeUserId = editingEmployee
    ? getEmployeeUserId(editingEmployee)
    : null;
  const isEditingCurrentUser = Boolean(
    editingEmployeeUserId &&
      currentUserId &&
      editingEmployeeUserId === currentUserId
  );

  const canEditOwnAvatar = useMemo(() => {
    if (!editingEmployee || !currentUserId) {
      return false;
    }

    const employeeUserId = getEmployeeUserId(editingEmployee);
    return Boolean(employeeUserId) && employeeUserId === currentUserId;
  }, [editingEmployee, currentUserId]);

  useEffect(() => {
    if (showPending) {
      setSelectedEmployees([]);
      setShowBulkCommunication(false);
      setSelectedEmployee(null);
    }
  }, [showPending]);

  useEffect(() => {
    if (!canUseBulkSelection) {
      setSelectedEmployees([]);
      setShowBulkCommunication(false);
    }
  }, [canUseBulkSelection]);

  const reloadEmployees = useCallback(async () => {
    setLoading(true);
    setRemovedLoading(true);
    setFetchError(null);
    setRemovedError(null);

    try {
      const [activeResult, removedResult] = await Promise.allSettled([
        fetchEmployeesFromApi(),
        fetchRemovedEmployeesFromApi(),
      ]);

      if (activeResult.status === 'fulfilled') {
        setEmployees(activeResult.value ?? []);
        setFetchError(null);
      } else {
        setEmployees([]);
        setFetchError(activeResult.reason);
      }

      if (removedResult.status === 'fulfilled') {
        setRemovedEmployees(
          sortRemovedEmployeesByDate(removedResult.value ?? [])
        );
        setRemovedError(null);
      } else {
        setRemovedEmployees([]);
        setRemovedError(removedResult.reason);
      }
    } finally {
      setLoading(false);
      setRemovedLoading(false);
    }
  }, []);

  const loadPendingInvitations = useCallback(async () => {
    setPendingLoading(true);
    setPendingError(null);
    setPendingActionError(null);
    try {
      const pending = await fetchPendingInvitations();
      setPendingInvitations(Array.isArray(pending) ? pending : []);
    } catch (error) {
      setPendingError(error);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadEmployees();
  }, [reloadEmployees]);

  useEffect(() => {
    loadPendingInvitations();
  }, [loadPendingInvitations]);

  useEffect(() => {
    if (!selectedLocation) {
      return;
    }

    const byId = locations.find(
      (location) => location?.id === selectedLocation?.id
    );
    if (byId && byId !== selectedLocation) {
      setSelectedLocation(byId);
      return;
    }

    if (!byId && selectedLocation?.name) {
      const byName = locations.find(
        (location) => location?.name === selectedLocation.name
      );
      if (byName && byName !== selectedLocation) {
        setSelectedLocation(byName);
      }
    }
  }, [locations, selectedLocation]);

  const openRevokeInvitationModal = useCallback((invitation) => {
    if (!invitation?.token) {
      return;
    }
    setPendingActionError(null);
    setPendingRevokeInvitation(invitation);
  }, []);

  const confirmRevokeInvitation = useCallback(async () => {
    if (!pendingRevokeInvitation?.token) {
      setPendingRevokeInvitation(null);
      return;
    }

    setPendingActionError(null);
    setRevokingToken(pendingRevokeInvitation.token);
    try {
      await revokeEmployeeInvitation(pendingRevokeInvitation.token);
      await loadPendingInvitations();
      setPendingRevokeInvitation(null);
    } catch (error) {
      setPendingActionError(error);
    } finally {
      setRevokingToken(null);
    }
  }, [loadPendingInvitations, pendingRevokeInvitation]);

  const cancelRevokeInvitation = useCallback(() => {
    setPendingRevokeInvitation(null);
  }, []);

  const locationFilters = useMemo(() => {
    const names = (employees ?? [])
      .flatMap((emp) => {
        if (Array.isArray(emp?.locationNames) && emp.locationNames.length > 0) {
          return emp.locationNames;
        }
        return typeof emp?.location === 'string' &&
          emp.location.trim().length > 0
          ? [emp.location.trim()]
          : [];
      })
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());

    return [...new Set(names)];
  }, [employees]);
  const roles = useMemo(
    () => [...new Set(employees?.map((emp) => emp?.role)?.filter(Boolean))],
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    return employees?.filter((employee) => {
      const matchesSearch =
        searchTerm === '' ||
        employee?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        employee?.role?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        employee?.department
          ?.toLowerCase()
          ?.includes(searchTerm?.toLowerCase()) ||
        employee?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesLocation =
        filters?.location === '' ||
        (Array.isArray(employee?.locationNames)
          ? employee.locationNames.includes(filters?.location)
          : employee?.location === filters?.location);
      const matchesDepartment =
        filters?.department === '' ||
        employee?.department === filters?.department;

      const matchesRole =
        filters?.role === '' ||
        employee?.role === filters?.role ||
        (filters?.role === 'Manager' &&
          (employee?.isManager ||
            (typeof employee?.role === 'string' &&
              employee.role.toLowerCase().includes('manager'))));

      const matchesStatus =
        filters?.status === '' || employee?.status === filters?.status;

      return (
        matchesSearch &&
        matchesLocation &&
        matchesDepartment &&
        matchesRole &&
        matchesStatus
      );
    });
  }, [employees, searchTerm, filters]);

  const statusCounts = useMemo(() => {
    return employees?.reduce((acc, employee) => {
      acc[employee?.status] = (acc[employee?.status] || 0) + 1;
      return acc;
    }, {});
  }, [employees]);

  useEffect(() => {
    setSelectedEmployees((prev) =>
      prev?.filter((id) => employees?.some((emp) => emp?.id === id))
    );
    if (
      selectedEmployee &&
      !employees?.some((emp) => emp?.id === selectedEmployee?.id)
    ) {
      setSelectedEmployee(null);
    }
  }, [employees, selectedEmployee]);

  const handleViewDetails = useCallback(
    async (employee) => {
      if (!employee) {
        return;
      }

      const employeeId = employee?.id;
      let detailedEmployee = employeeId
        ? employeeDetailsCache?.[employeeId] ?? null
        : null;

      if (employeeId && !detailedEmployee) {
        try {
          const fetched = await fetchEmployeeById(employeeId);
          if (fetched) {
            detailedEmployee = fetched;
            setEmployeeDetailsCache((prev) => ({
              ...prev,
              [employeeId]: fetched,
            }));
          }
        } catch (error) {
          detailedEmployee = null;
        }
      }

      const effectiveEmployee = detailedEmployee ?? employee;
      const effectiveEmployeeId = effectiveEmployee?.id ?? employee?.id ?? null;

      const documents = Array.isArray(effectiveEmployee?.documents)
        ? effectiveEmployee.documents
        : Array.isArray(employee?.documents)
        ? employee.documents
        : [];

      const hasTimeOffData = Array.isArray(timeOffRequests);
      const needsAbsencesFetch = !hasTimeOffData && !timeOffLoading;
      const initialAbsences = hasTimeOffData
        ? filterAbsencesForEmployee(timeOffRequests, effectiveEmployee)
        : [];
      const initialAbsencesError = hasTimeOffData
        ? null
        : needsAbsencesFetch || timeOffLoading
        ? null
        : timeOffError;

      if (needsAbsencesFetch) {
        setTimeOffLoading(true);
        setTimeOffError(null);
      }

      setSelectedEmployee({
        ...effectiveEmployee,
        documents,
        absences: initialAbsences,
        absencesLoading: !hasTimeOffData,
        absencesError: initialAbsencesError,
      });

      if (needsAbsencesFetch) {
        try {
          const requests = await fetchTimeOffRequests();
          const normalized = Array.isArray(requests) ? requests : [];
          setTimeOffRequests(normalized);
          setTimeOffError(null);
          setSelectedEmployee((prev) => {
            if (!prev || prev?.id !== effectiveEmployeeId) {
              return prev;
            }
            return {
              ...prev,
              absences: filterAbsencesForEmployee(
                normalized,
                effectiveEmployee
              ),
              absencesLoading: false,
              absencesError: null,
            };
          });
        } catch (error) {
          setTimeOffError(error);
          setSelectedEmployee((prev) => {
            if (!prev || prev?.id !== effectiveEmployeeId) {
              return prev;
            }
            return {
              ...prev,
              absences: [],
              absencesLoading: false,
              absencesError: error,
            };
          });
        } finally {
          setTimeOffLoading(false);
        }
      }
    },
    [employeeDetailsCache, timeOffRequests, timeOffLoading, timeOffError]
  );

  const handleMessage = useCallback(
    (employee) => {
      if (!employee) {
        return;
      }

      const recipientId = resolveMessagingRecipientIdFromEmployee(employee);

      if (!recipientId) {
        console.warn(
          'Unable to determine messaging recipient for employee',
          employee
        );
        return;
      }

      navigate('/internal-messaging', {
        state: {
          directRecipientId: recipientId,
          directRecipientName: employee?.name ?? employee?.fullName ?? null,
          directRecipientEmail: employee?.email ?? null,
        },
      });
    },
    [navigate]
  );

  const handleCall = (employee) => {
    console.log('Calling employee:', employee?.phone);
  };

  const handleSelectEmployee = (employeeId) => {
    if (!canUseBulkSelection) {
      return;
    }

    setSelectedEmployees((prev) =>
      prev?.includes(employeeId)
        ? prev?.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (!canUseBulkSelection) {
      return;
    }

    if (selectedEmployees?.length === filteredEmployees?.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees?.map((emp) => emp?.id));
    }
  };

  const updateLocationFromFilters = useCallback(
    (nextFilters) => {
      if (
        !nextFilters ||
        !Object.prototype.hasOwnProperty.call(nextFilters, 'location')
      ) {
        return;
      }

      const rawName = nextFilters.location;
      const nextLocationName =
        typeof rawName === 'string' ? rawName.trim() : '';
      const currentLocationName = selectedLocation?.name?.trim() ?? '';

      if (nextLocationName === currentLocationName) {
        return;
      }

      if (!nextLocationName) {
        setSelectedLocation(null);
        return;
      }

      const matchedLocation = locations.find(
        (loc) => loc?.name === nextLocationName
      );
      if (matchedLocation) {
        setSelectedLocation(matchedLocation);
      } else {
        setSelectedLocation({ id: nextLocationName, name: nextLocationName });
      }
    },
    [locations, selectedLocation]
  );

  const handleFiltersChange = useCallback(
    (nextFiltersOrUpdater) => {
      if (typeof nextFiltersOrUpdater === 'function') {
        setFilters((prev) => {
          const nextFilters = nextFiltersOrUpdater(prev);
          updateLocationFromFilters(nextFilters);
          return nextFilters;
        });
        return;
      }

      const nextFilters = nextFiltersOrUpdater ?? {};
      setFilters(nextFilters);
      updateLocationFromFilters(nextFilters);
    },
    [updateLocationFromFilters]
  );

  const handleClearFilters = () => {
    const clearedFilters = {
      location: '',
      department: '',
      role: '',
      status: '',
    };
    setFilters(clearedFilters);
    setSelectedLocation(null);
    setSearchTerm('');
  };

  const handleHeaderLocationChange = useCallback((location) => {
    setSelectedLocation(location ?? null);
    setFilters((prev) => {
      const nextLocationName = location?.name ?? '';
      if ((prev?.location ?? '') === nextLocationName) {
        return prev;
      }
      return {
        ...prev,
        location: nextLocationName,
      };
    });
  }, []);

  const handleBulkMessage = (messageData) => {
    console.log('Sending bulk message:', messageData);
    setSelectedEmployees([]);
  };

  const handleInviteEmployee = () => {
    setIsInviteOpen(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setIsFormOpen(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!employeeId) {
      return false;
    }
    const employee = employees?.find((emp) => emp?.id === employeeId);
    if (!employee) {
      return false;
    }
    if (!canDeleteEmployee(employee)) {
      setEmployeeActionError(
        new Error(
          t(
            'employeeDirectory:actions.deleteForbidden',
            'You do not have permission to delete this employee.'
          )
        )
      );
      return false;
    }
    setEmployeeActionError(null);

    const shouldDelete = window?.confirm?.(
      formatTranslation(
        t(
          'employeeDirectory:actions.confirmRemoval',
          'Remove {{name}} from the directory?'
        ),
        {
          name:
            employee?.name ??
            t('employeeDirectory:actions.thisEmployee', 'this employee'),
        }
      )
    );

    if (!shouldDelete) {
      return false;
    }

    try {
      const removalResult = await deleteEmployeeFromApi(employeeId);

      if (removalResult) {
        setRemovedEmployees((prev) =>
          sortRemovedEmployeesByDate([
            ...prev.filter((emp) => emp?.id !== removalResult?.id),
            removalResult,
          ])
        );
      } else {
        setRemovedEmployees((prev) => sortRemovedEmployeesByDate(prev));
        reloadEmployees().catch(() => {});
      }
    } catch (error) {
      setEmployeeActionError(error);
      return false;
    }

    setEmployees((prev) => prev?.filter((emp) => emp?.id !== employeeId));
    setSelectedEmployees((prev) => prev?.filter((id) => id !== employeeId));

    setSelectedEmployee((prev) => (prev?.id === employeeId ? null : prev));
    setEmployeeDetailsCache((prev) => {
      if (!prev || typeof prev !== 'object') {
        return prev;
      }
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });

    setRemovedSectionOpen(true);

    return true;
  };

  const handleRestoreEmployee = useCallback(
    async (employeeId) => {
      if (!employeeId) {
        return false;
      }

      const removedEmployee = removedEmployees?.find(
        (emp) => emp?.id === employeeId
      );
      if (!removedEmployee) {
        return false;
      }

      setEmployeeActionError(null);
      setRestoringEmployeeId(employeeId);

      try {
        await restoreEmployeeFromApi(employeeId);
        setEmployeeDetailsCache((prev) => {
          if (!prev || typeof prev !== 'object') {
            return prev;
          }
          const next = { ...prev };
          delete next[employeeId];
          return next;
        });
        await reloadEmployees();
        return true;
      } catch (error) {
        setEmployeeActionError(error);
        return false;
      } finally {
        setRestoringEmployeeId(null);
      }
    },
    [removedEmployees, reloadEmployees, restoreEmployeeFromApi]
  );

  const canDeleteEmployee = useCallback(
    (employee) => {
      if (!employee) {
        return false;
      }

      const normalizedUserRole =
        typeof user?.role === 'string' ? user.role.toLowerCase().trim() : '';
      const normalizedUserTags = Array.isArray(user?.tags)
        ? user.tags
            .map((tag) =>
              typeof tag === 'string' ? tag.toLowerCase().trim() : ''
            )
            .filter((tag) => tag.length > 0)
        : [];

      const isAdminUser =
        normalizedUserRole === 'admin' || normalizedUserTags.includes('admin');
      const isManagerUser =
        normalizedUserRole === 'manager' ||
        normalizedUserRole.includes('manager') ||
        normalizedUserTags.includes('manager');

      if (!isAdminUser && !isManagerUser) {
        return false;
      }

      const employeeUserId = getEmployeeUserId(employee);
      if (employeeUserId && currentUserId && employeeUserId === currentUserId) {
        return false;
      }

      const employeeRoleString = (() => {
        if (typeof employee?.raw?.user?.role === 'string') {
          return employee.raw.user.role.toLowerCase();
        }
        if (typeof employee?.role === 'string') {
          return employee.role.toLowerCase();
        }
        return '';
      })();

      const employeeTags = Array.isArray(employee?.tags)
        ? employee.tags
            .map((tag) =>
              typeof tag === 'string' ? tag.toLowerCase().trim() : ''
            )
            .filter((tag) => tag.length > 0)
        : [];

      const isTargetManager =
        employeeRoleString.includes('manager') ||
        Boolean(employee?.isManager) ||
        employeeTags.includes('manager');

      if (isTargetManager && !isAdminUser) {
        return false;
      }

      return true;
    },
    [user, currentUserId]
  );

  const handleSaveEmployee = async (employeeData) => {
    if (!employeeData) {
      return;
    }

    const isUpdate = Boolean(employeeData?.id);
    setEmployeeActionError(null);

    try {
      if (isUpdate) {
        const payload = buildEmployeeUpdatePayload(employeeData);
        const updatedEmployee = await updateEmployeeFromApi(
          employeeData.id,
          payload
        );

        const previousEmployee = employees?.find(
          (emp) => emp?.id === employeeData.id
        );
        const mergedEmployee = {
          ...(previousEmployee ?? {}),
          ...updatedEmployee,
          documents:
            previousEmployee?.documents ?? employeeData?.documents ?? [],
        };

        setEmployees((prev) =>
          prev?.map((emp) =>
            emp?.id === employeeData.id ? mergedEmployee : emp
          )
        );

        if (selectedEmployee?.id === employeeData.id) {
          setSelectedEmployee((prev) =>
            prev ? { ...prev, ...mergedEmployee } : prev
          );
        }
      } else {
        setEmployees((prev) => {
          return [...prev, { ...employeeData, id: createLocalId() }];
        });
      }

      setIsFormOpen(false);
      setEditingEmployee(null);
    } catch (error) {
      setEmployeeActionError(error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole="manager"
        selectedLocation={selectedLocation}
        locations={locations}
        onLocationChange={handleHeaderLocationChange}
        notifications={{
          messages: 3,
          timeOff: 2,
        }}
      />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {locationsError && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg px-4 py-3 text-sm text-error flex items-center justify-between">
              <span>
                {t(
                  'employeeDirectory:errors.locations',
                  'Unable to load locations.'
                )}
              </span>
              <button
                type="button"
                className="underline"
                onClick={() => refetchLocations().catch(() => {})}
              >
                {t('actions.tryAgain', 'Try again')}
              </button>
            </div>
          )}
          <div className="mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {t('employeeDirectory:page.title', 'Employee Directory')}
                </h1>
                <p className="text-muted-foreground mt-2">
                  {t(
                    'employeeDirectory:page.subtitle',
                    'Manage and communicate with your restaurant staff across all locations'
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button variant="default" onClick={handleInviteEmployee}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  {t(
                    'employeeDirectory:page.actions.invite',
                    'Invite Employee'
                  )}
                </Button>
                {!showPending && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPending(true);
                      setSelectedEmployees([]);
                    }}
                    disabled={pendingLoading}
                  >
                    <Icon name="Clock" size={16} className="mr-2" />
                    {t(
                      'employeeDirectory:page.actions.viewPending',
                      'Pending Invitations'
                    )}
                  </Button>
                )}
                {showPending && (
                  <Button
                    variant="outline"
                    onClick={() => setShowPending(false)}
                  >
                    <Icon name="Users" size={16} className="mr-2" />
                    {t(
                      'employeeDirectory:page.actions.backToEmployees',
                      'Back to Employees'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {fetchError && (
            <div className="mb-6">
              <div className="bg-error/10 border border-error/20 rounded-lg px-4 py-3 text-sm text-error">
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <p className="font-medium">
                      {t(
                        'employeeDirectory:errors.employees',
                        'Unable to load employees.'
                      )}
                    </p>
                    <p className="mt-1 text-error/80">
                      {Array.isArray(fetchError?.response?.data?.message)
                        ? fetchError.response.data.message.join('. ')
                        : fetchError?.response?.data?.message ??
                          fetchError?.message}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={reloadEmployees}>
                    {t('employeeDirectory:actions.retry', 'Retry')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="hidden space-y-6 lg:col-span-1 lg:block">
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  {t('employeeDirectory:stats.title', 'Quick Stats')}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t(
                        'employeeDirectory:stats.totalEmployees',
                        'Total Employees'
                      )}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {employees?.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t(
                        'employeeDirectory:stats.pendingInvites',
                        'Pending Invitations'
                      )}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {pendingInvitations?.length ?? 0}
                    </span>
                  </div>
                  {STATUS_OPTIONS?.map((option) => (
                    <div
                      key={option?.value}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm text-muted-foreground">
                        {t(
                          `employeeDirectory:statuses.${option?.key}`,
                          option?.fallback
                        )}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {statusCounts?.[option?.value] ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              {employeeActionError && (
                <div className="border border-error/30 bg-error/5 text-error rounded-lg p-4 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <span>
                      {extractErrorMessage(employeeActionError) ||
                        t(
                          'employeeDirectory:errors.generic',
                          'Something went wrong.'
                        )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEmployeeActionError(null)}
                    >
                      {t('employeeDirectory:actions.dismiss', 'Dismiss')}
                    </Button>
                  </div>
                </div>
              )}
              {showPending ? (
                <div className="space-y-4">
                  {pendingLoading ? (
                    <div className="bg-card border border-border rounded-lg p-12">
                      <LoadingState
                        message={t(
                          'employeeDirectory:pending.loading',
                          'Loading pending invitations…'
                        )}
                        heightClassName="min-h-[8rem]"
                      />
                    </div>
                  ) : pendingError ? (
                    <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
                      <Icon
                        name="AlertTriangle"
                        size={32}
                        className="text-error mx-auto mb-3"
                      />
                      <h3 className="text-lg font-semibold text-error-foreground mb-2">
                        {t(
                          'employeeDirectory:pending.errorTitle',
                          'Unable to load pending invitations'
                        )}
                      </h3>
                      <p className="text-sm text-error/80 mb-4">
                        {Array.isArray(pendingError?.response?.data?.message)
                          ? pendingError.response.data.message.join('. ')
                          : pendingError?.response?.data?.message ??
                            pendingError?.message}
                      </p>
                      <Button
                        variant="outline"
                        onClick={loadPendingInvitations}
                      >
                        {t('employeeDirectory:actions.retry', 'Retry')}
                      </Button>
                    </div>
                  ) : pendingInvitations?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-12 text-center">
                      <Icon
                        name="Mail"
                        size={48}
                        className="text-muted-foreground mx-auto mb-4"
                      />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {t(
                          'employeeDirectory:pending.emptyTitle',
                          'No Pending Invitations'
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'employeeDirectory:pending.emptyDescription',
                          'Invite new team members to see them appear here.'
                        )}
                      </p>
                    </div>
                  ) : (
                    <>
                      {pendingActionError && !pendingRevokeInvitation && (
                        <div className="border border-error/30 bg-error/5 text-error rounded-lg p-4 text-sm mb-4">
                          {extractErrorMessage(pendingActionError) ||
                            t(
                              'employeeDirectory:errors.generic',
                              'Something went wrong.'
                            )}
                        </div>
                      )}
                      {pendingInvitations?.map((invitation) => (
                        <div
                          key={invitation?.id ?? invitation?.invitation}
                          className="bg-card border border-border rounded-lg p-6"
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-3">
                                <Icon
                                  name="UserRound"
                                  size={20}
                                  className="text-primary"
                                />
                                <div>
                                  <p className="text-base font-semibold text-foreground">
                                    {invitation?.invitedName ??
                                      invitation?.email}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    {invitation?.email}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                              {invitation?.role && (
                                <span className="bg-muted px-3 py-1 rounded-full">
                                  {invitation.role}
                                </span>
                              )}
                              {invitation?.department && (
                                <span className="bg-muted px-3 py-1 rounded-full">
                                  {invitation.department}
                                </span>
                              )}
                              {invitation?.location && (
                                <span className="bg-muted px-3 py-1 rounded-full">
                                  {invitation.location}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                            <div>
                              <span className="font-medium text-foreground block">
                                {t(
                                  'employeeDirectory:pending.invitedBy',
                                  'Invited By'
                                )}
                              </span>
                              {invitation?.invitedByName ?? '—'}
                            </div>
                            <div>
                              <span className="font-medium text-foreground block">
                                {t(
                                  'employeeDirectory:pending.startDate',
                                  'Start Date'
                                )}
                              </span>
                              {invitation?.startDate
                                ? new Date(
                                    invitation.startDate
                                  ).toLocaleDateString()
                                : '—'}
                            </div>
                            <div>
                              <span className="font-medium text-foreground block">
                                {t(
                                  'employeeDirectory:pending.expires',
                                  'Expires'
                                )}
                              </span>
                              {invitation?.expiresAt
                                ? new Date(
                                    invitation.expiresAt
                                  ).toLocaleDateString()
                                : '—'}
                            </div>
                          </div>
                          <div className="mt-6 flex justify-end">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() =>
                                openRevokeInvitationModal(invitation)
                              }
                              disabled={
                                !invitation?.token ||
                                revokingToken === invitation?.token
                              }
                            >
                              {revokingToken === invitation?.token
                                ? t(
                                    'employeeDirectory:pending.revoking',
                                    'Revoking…'
                                  )
                                : t(
                                    'employeeDirectory:pending.revokeAction',
                                    'Revoke Invitation'
                                  )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <SearchAndFilters
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={handleClearFilters}
                    employeeCount={filteredEmployees?.length}
                    locations={locationFilters}
                    roles={roles}
                  />

                  {canUseBulkSelection && selectedEmployees?.length > 0 && (
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            checked={
                              selectedEmployees?.length ===
                              filteredEmployees?.length
                            }
                            onChange={handleSelectAll}
                          />
                          <span className="text-sm font-medium text-foreground">
                            {formatTranslation(
                              t(
                                `employeeDirectory:bulkSelection.count.${
                                  selectedEmployees?.length === 1
                                    ? 'singular'
                                    : 'plural'
                                }`,
                                selectedEmployees?.length === 1
                                  ? '{{count}} employee selected'
                                  : '{{count}} employees selected'
                              ),
                              { count: selectedEmployees?.length ?? 0 }
                            )}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedEmployees([])}
                          >
                            {t(
                              'employeeDirectory:bulkSelection.clear',
                              'Clear Selection'
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setShowBulkCommunication(true)}
                          >
                            <Icon
                              name="MessageSquare"
                              size={14}
                              className="mr-2"
                            />
                            {t(
                              'employeeDirectory:bulkSelection.send',
                              'Send Message'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {loading ? (
                      <div className="bg-card border border-border rounded-lg p-12">
                        <LoadingState
                          message={t(
                            'employeeDirectory:page.loadingEmployees',
                            'Loading employees…'
                          )}
                          heightClassName="min-h-[12rem]"
                        />
                      </div>
                    ) : filteredEmployees?.length === 0 ? (
                      <div className="bg-card border border-border rounded-lg p-12 text-center">
                        <Icon
                          name="Users"
                          size={48}
                          className="text-muted-foreground mx-auto mb-4"
                        />
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {t(
                            'employeeDirectory:empty.title',
                            'No Employees Found'
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          {t(
                            'employeeDirectory:empty.description',
                            'Try adjusting your search terms or filters to find employees.'
                          )}
                        </p>
                        <Button variant="outline" onClick={handleClearFilters}>
                          {t(
                            'employeeDirectory:empty.clearFilters',
                            'Clear All Filters'
                          )}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4">
                          {filteredEmployees?.map((employee) => (
                            <div
                              key={employee?.id}
                              className="flex items-start space-x-3"
                            >
                              {canUseBulkSelection && (
                                <div className="pt-4">
                                  <Checkbox
                                    checked={selectedEmployees?.includes(
                                      employee?.id
                                    )}
                                    onChange={() =>
                                      handleSelectEmployee(employee?.id)
                                    }
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <EmployeeCard
                                  employee={employee}
                                  onViewDetails={handleViewDetails}
                                  onMessage={handleMessage}
                                  onCall={handleCall}
                                  onEdit={handleEditEmployee}
                                  isCurrentUser={
                                    !!currentUserId &&
                                    getEmployeeUserId(employee) ===
                                      currentUserId
                                  }
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="bg-card border border-border rounded-lg">
                    <button
                      type="button"
                      className="w-full px-4 py-3 flex items-center justify-between text-left"
                      onClick={() => setRemovedSectionOpen((prev) => !prev)}
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          name="UserX"
                          size={18}
                          className="text-muted-foreground"
                        />
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {t(
                              'employeeDirectory:removed.title',
                              'Removed Employees'
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatTranslation(
                              t(
                                `employeeDirectory:removed.subtitle.${
                                  removedEmployees?.length === 1
                                    ? 'singular'
                                    : 'plural'
                                }`,
                                removedEmployees?.length === 1
                                  ? '{{count}} employee flagged for removal'
                                  : '{{count}} employees flagged for removal'
                              ),
                              { count: removedEmployees?.length ?? 0 }
                            )}
                          </p>
                        </div>
                      </div>
                      <Icon
                        name={removedSectionOpen ? 'ChevronUp' : 'ChevronDown'}
                        size={16}
                        className="text-muted-foreground"
                      />
                    </button>
                    {removedSectionOpen && (
                      <div className="border-t border-border p-4 space-y-4">
                        {removedLoading ? (
                          <LoadingState
                            message={t(
                              'employeeDirectory:removed.loading',
                              'Loading removed employees…'
                            )}
                            heightClassName="min-h-[6rem]"
                          />
                        ) : removedError ? (
                          <div className="border border-error/30 bg-error/5 text-error rounded-lg p-4 text-sm flex flex-col gap-3">
                            <span>
                              {extractErrorMessage(removedError) ||
                                t(
                                  'employeeDirectory:errors.generic',
                                  'Something went wrong.'
                                )}
                            </span>
                            <div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  reloadEmployees().catch(() => {})
                                }
                              >
                                {t('employeeDirectory:actions.retry', 'Retry')}
                              </Button>
                            </div>
                          </div>
                        ) : removedEmployees?.length === 0 ? (
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <Icon
                              name="CheckCircle2"
                              size={16}
                              className="text-primary"
                            />
                            <span>
                              {t(
                                'employeeDirectory:removed.empty',
                                'No employees have been removed in the last 30 days.'
                              )}
                            </span>
                          </div>
                        ) : (
                          removedEmployees.map((employee) => {
                            const removedDate = parseTimestamp(
                              employee?.removedAt
                            );
                            const status = getRemovalStatus(
                              employee?.removedAt,
                              t,
                              language
                            );
                            const undoLabel = status.expired
                              ? t(
                                  'employeeDirectory:removed.undoUnavailableAction',
                                  'Undo Unavailable'
                                )
                              : t(
                                  'employeeDirectory:removed.undoAction',
                                  'Undo Removal'
                                );
                            const statusDetail = `${status.timeText}${
                              status.detailText ? ` ${status.detailText}` : ''
                            }`;

                            return (
                              <div
                                key={employee?.id}
                                className="border border-border/60 rounded-lg p-4 bg-muted/20"
                              >
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                      <Icon
                                        name="UserCircle2"
                                        size={20}
                                        className="text-muted-foreground"
                                      />
                                      <div>
                                        <p className="text-base font-semibold text-foreground">
                                          {employee?.name ??
                                            employee?.fullName ??
                                            t(
                                              'employeeDirectory:card.unknownName',
                                              'Employee'
                                            )}
                                        </p>
                                        <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
                                          {employee?.role && (
                                            <span>{employee.role}</span>
                                          )}
                                          {employee?.department && (
                                            <>
                                              <span>•</span>
                                              <span>{employee.department}</span>
                                            </>
                                          )}
                                          {employee?.location && (
                                            <>
                                              <span>•</span>
                                              <span>{employee.location}</span>
                                            </>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {removedDate
                                        ? formatTranslation(
                                            t(
                                              'employeeDirectory:removed.removedOn',
                                              'Removed on {{date}}'
                                            ),
                                            {
                                              date: removedDate.toLocaleString(
                                                language || undefined
                                              ),
                                            }
                                          )
                                        : t(
                                            'employeeDirectory:removed.dateUnavailable',
                                            'Removal date unavailable'
                                          )}
                                    </p>
                                  </div>
                                  <div className="flex flex-col items-start md:items-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        handleRestoreEmployee(employee?.id)
                                      }
                                      disabled={
                                        status.expired ||
                                        restoringEmployeeId === employee?.id
                                      }
                                    >
                                      {restoringEmployeeId === employee?.id
                                        ? t(
                                            'employeeDirectory:removed.restoring',
                                            'Restoring…'
                                          )
                                        : undoLabel}
                                    </Button>
                                    <p className="text-xs text-muted-foreground max-w-xs text-left md:text-right">
                                      {statusDetail}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedEmployee && (
        <EmployeeDetailPanel
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          onMessage={handleMessage}
          onCall={handleCall}
        />
      )}

      {canUseBulkSelection && showBulkCommunication && (
        <BulkCommunicationPanel
          selectedEmployees={selectedEmployees}
          allEmployees={employees}
          onClose={() => setShowBulkCommunication(false)}
          onSendMessage={handleBulkMessage}
        />
      )}

      {isFormOpen && (
        <EmployeeFormModal
          employee={editingEmployee}
          onClose={() => {
            setIsFormOpen(false);
            setEditingEmployee(null);
          }}
          onSubmit={handleSaveEmployee}
          canDelete={canDeleteEmployee(editingEmployee)}
          canEditAvatar={canEditOwnAvatar}
          availableBranches={locations}
          branchesLoading={locationsLoading}
          allowRoleSelection={!isEditingCurrentUser}
          allowBranchSelection={!isEditingCurrentUser}
          onDelete={async (employeeId) => {
            if (!employeeId) {
              return;
            }
            const removed = await handleDeleteEmployee(employeeId);
            if (removed) {
              setIsFormOpen(false);
              setEditingEmployee(null);
            }
          }}
        />
      )}

      {pendingRevokeInvitation && (
        <ConfirmRevokeInvitationModal
          invitation={pendingRevokeInvitation}
          onConfirm={confirmRevokeInvitation}
          onCancel={cancelRevokeInvitation}
          loading={revokingToken === pendingRevokeInvitation?.token}
          errorMessage={extractErrorMessage(pendingActionError)}
        />
      )}

      {isInviteOpen && (
        <InviteEmployeeModal
          defaultLocation={selectedLocation}
          onClose={() => setIsInviteOpen(false)}
          onInvitationCreated={() => {
            loadPendingInvitations();
          }}
        />
      )}
    </div>
  );
};

export default EmployeeDirectory;
